/**
 * ProtectedRoute — redirects unauthenticated users to login.
 * Optionally enforces required permissions via the `requiredPermissions` prop.
 *
 * @module apps/web/components/ProtectedRoute
 * @since W2.9
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/auth.js';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If set, user must hold at least one of these permissions */
  requiredPermissions?: string[];
}

export function ProtectedRoute({ children, requiredPermissions }: ProtectedRouteProps) {
  const { authenticated, loading, claims } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-space-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-brand-indigo border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 text-sm">Loading…</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          returnTo: `${location.pathname}${location.search}${location.hash}`,
          autoStart: true,
        }}
      />
    );
  }

  // Permission gate
  if (requiredPermissions && requiredPermissions.length > 0) {
    const userPermissions = claims?.permissions ?? [];
    const hasAccess = requiredPermissions.some((p) => userPermissions.includes(p));
    if (!hasAccess) {
      return <Navigate to="/forbidden" replace />;
    }
  }

  return <>{children}</>;
}
